/**
 * This file is largely copied from fetchMRUSuggestions.ts in owa-attachment-suggestions.
 * We are copying these files because this feature is still in a volatile state, so we wish to avoid
 * attempting to refactor the code so that it can be used here.
 * There is also ongoing experimentation with the Attachment Suggestions feature.
 */
import convertMRUAttachmentToReplyWithAttachmentSuggestion from '../utils/convertMRUAttachmentToReplyWithAttachmentSuggestion';
import MRUAttachment from '../store/schema/MRUAttachment';
import RequestStatus from '../store/schema/RequestStatus';
import { getAccessTokenforResourceAsLazy } from 'owa-tokenprovider';
import { getDefaultAnchorMailboxCookie } from 'owa-ows-gateway/lib/anchormailbox';
import { logUnsampledDatapoint } from 'owa-smart-pill';
import { ReplyWithAttachmentSuggestion } from 'owa-mail-store';
import { setStoreStatus, setSuggestions } from '../actions/smartDocSuggestionAction';
import { trace } from 'owa-trace';

const CONTENT_TYPE = 'application/json;charset=utf-8';
const MRU_ENDPOINT = '/search/api/v1/recommendations';

// Used to retrieve contextual attachment suggestion
const CONTEXTUAL_SUGGESTION_FLIGHT = 'suggestedattachments';

/*
 * onServiceCallStateChanged can be used to ensure that only one call is made to this service at a time.
 */
export default async function fetchMRUSuggestions(
    referenceItemId: string
): Promise<ReplyWithAttachmentSuggestion[] | null> {
    let suggestions: ReplyWithAttachmentSuggestion[] = null;
    let errorMessage: string = '';

    // Start processing the request.
    setStoreStatus(RequestStatus.Pending);
    try {
        // Formulate the request
        const token: string = await getTokenForMRUService();
        const requestInit: RequestInit = createRequestInit(token, referenceItemId);

        const response: Response = await fetch(MRU_ENDPOINT, requestInit);

        if (response && response.status == 200 /* OK */) {
            const responseBody = await response.text();
            const parsedResponse = JSON.parse(responseBody);

            suggestions = getAttachmentsFromResponse(parsedResponse, !!referenceItemId);
        } else {
            if (!response) {
                errorMessage = 'No response';
            } else {
                errorMessage = response.status + '-' + response.statusText;
            }
        }
    } catch (e) {
        errorMessage = e.message;
    } finally {
        if (errorMessage) {
            trace.info(`getMRUSuggestions: Error ${errorMessage}`);
            // Log the dataPoint while the MRU suggestions failed to load.
            logUnsampledDatapoint('SmartDocPillError', [
                `getMRUSuggestions: Error ${errorMessage}`,
            ]);
            setStoreStatus(RequestStatus.Error);
        } else {
            // Update the suggestion and mark the request as complete.
            setSuggestions(suggestions, RequestStatus.Complete);
        }

        return suggestions;
    }
}

async function getTokenForMRUService(): Promise<string> {
    let [token, tokenPromise] = getAccessTokenforResourceAsLazy(window.location.origin);

    // If token is not returned synchronously, we need to await on the tokenPromise
    if (!token) {
        token = await tokenPromise;
    }

    return token;
}

function createRequestInit(token: string, referenceItemId: string): RequestInit {
    const requestInit: RequestInit = {};
    requestInit.headers = new Headers();

    // Authorization
    (<Headers>requestInit.headers).set('Authorization', `Bearer ${token}`);

    // Content-Type
    (<Headers>requestInit.headers).set('Content-Type', CONTENT_TYPE);

    // Method
    requestInit.method = 'POST';

    // X-AnchorMailbox
    const defaultAnchorMailbox = getDefaultAnchorMailboxCookie();
    if (defaultAnchorMailbox) {
        (<Headers>requestInit.headers).append('X-AnchorMailbox', defaultAnchorMailbox);
    }

    // X-Client-LocalTime
    (<Headers>requestInit.headers).set('X-Client-LocalTime', new Date().toISOString());

    const xClientFlightsHeaders: string[] = [];

    if (referenceItemId) {
        xClientFlightsHeaders.push(CONTEXTUAL_SUGGESTION_FLIGHT);
    }

    if (xClientFlightsHeaders.length) {
        (<Headers>requestInit.headers).set('X-Client-Flights', xClientFlightsHeaders.join(','));
    }

    const data = {
        EntityRequests: [
            referenceItemId
                ? {
                      Context: {
                          EntityId: referenceItemId,
                      },
                  }
                : {},
        ],
        Scenario: {
            Name: 'AttachFile',
        },
    };

    requestInit.body = JSON.stringify(data);

    return requestInit;
}

function getAttachmentsFromResponse(
    parsedResponse: any,
    isMRUContextual: boolean
): ReplyWithAttachmentSuggestion[] {
    try {
        // MRU attachments
        const rawMRUAttachments: MRUAttachment[] =
            parsedResponse.EntitySets[0].ResultSets[0].Results;

        // Convert to ReplyWithAttachmentSuggestions.
        return rawMRUAttachments.map((mruAttachment, index) =>
            convertMRUAttachmentToReplyWithAttachmentSuggestion(
                mruAttachment,
                index,
                isMRUContextual
            )
        );
    } catch (e) {
        trace.info(`[fetchMRUSuggestions_getAttachmentsFromResponse] Error: ${e.message}`);
        return [];
    }
}
